import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'

import type { IP360PartnerFlags } from 'partner360/models/p360PartnerFlags/IP360PartnerFlags'

import type {
  ICockpitPartnerData,
  ICockpitPartnerOfferingsCatalog,
  TCockpitRtm,
} from '../model/types'

import { getEnabledCockpitOfferings } from './getEnabledCockpitOfferings'

const TOP_PARTNER_OFFERING_FALLBACKS: Partial<Record<string, ProgramTypes>> = {
  WWBPS: ProgramTypes.PersonalSystems,
  WWOPH: ProgramTypes.CorePrint,
}

interface IGetCockpitPartnerClassOptionsParams {
  partnerClassFlags: IP360PartnerFlags[]
  partnerData: ICockpitPartnerData[]
  offeringsCatalog: ICockpitPartnerOfferingsCatalog[]
  selectedRtm: TCockpitRtm
}

const normalizeOffering = (value: string) => value.trim().toLowerCase()

const isPartnerInRtm = (partner: ICockpitPartnerData, selectedRtm: TCockpitRtm) =>
  selectedRtm === 'resellers' ? partner.IsCommercial === 1 : partner.IsDistributor === 1

const getAvailableOfferings = (
  partnerData: ICockpitPartnerData[],
  offeringsCatalog: ICockpitPartnerOfferingsCatalog[],
  selectedRtm: TCockpitRtm
) => {
  const partnerIds = new Set(
    partnerData
      .filter((partner) => isPartnerInRtm(partner, selectedRtm))
      .map((partner) => partner.PartnerId.trim())
  )

  return new Set(
    getEnabledCockpitOfferings(
      offeringsCatalog.filter(({ partnerId }) => partnerIds.has(partnerId.trim()))
    ).map(({ offering }) => normalizeOffering(offering))
  )
}

const getFlagOffering = ({ Offering, PartnerFlag }: IP360PartnerFlags) =>
  Offering.trim() || TOP_PARTNER_OFFERING_FALLBACKS[PartnerFlag] || ''

/** Partner classes applicable to the selected RTM. They do not follow the selected offering: a
 *  class tied to an offering stays available while at least one partner of the RTM has that
 *  offering enabled. The reference-data Offering field is authoritative; the two Top filters
 *  retain their known offering as a fallback when an older reference response leaves that field
 *  empty. */
export const getCockpitPartnerClassOptions = ({
  partnerClassFlags,
  partnerData,
  offeringsCatalog,
  selectedRtm,
}: IGetCockpitPartnerClassOptionsParams) => {
  const reportRtm = selectedRtm === 'resellers' ? 'Reseller' : 'Distributor'
  const availableOfferings = getAvailableOfferings(partnerData, offeringsCatalog, selectedRtm)

  return partnerClassFlags
    .filter((flag) => flag.RTM === reportRtm)
    .filter((flag) => {
      const flagOffering = getFlagOffering(flag)

      return !flagOffering || availableOfferings.has(normalizeOffering(flagOffering))
    })
    .filter(
      (flag, index, source) =>
        source.findIndex((candidate) => candidate.BitValue === flag.BitValue) === index
    )
}
